import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Item } from '@/types/item';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import { transformDateString, transformDateStringFrance } from '@/utilities/datetime';
import { capitalizeFirstLetter } from '@/utilities/string';
import ModalView from '@/components/modals/Modal';
import { getItemShippingStatusWarehouseUpdateUrl } from '@/external/timeline/helper';
import { useActionTracking, useGenerateShippingLabelUrl, useGetSellerShippingStatus, useItem } from '@/hooks/item';

const StandardShippingId = 1;
const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);

  const { refetch: refetchItem } = useItem(item.productId, false);

  const mutateTracking = useActionTracking(item.productId);

  const nonSponsoredShippingLabelMutation = useGenerateShippingLabelUrl(item.productId);

  const {
    data: sellerShippingStatus,
    refetch: fetchSellerShippingStatus,
    isFetching: isFetchingSellerShippingStatus,
  } = useGetSellerShippingStatus(item.productId);

  const {
    data: shippedWithinDays,
    refetch: fetchShippedWithinDays,
    isFetching: isFetchingShippedWithinDays,
  } = useGetSellerShippingStatus(item.productId, ['shipment']);

  useEffect(() => {
    switch (nonSponsoredShippingLabelMutation.status) {
      case 'loading':
        setModal({
          kind: 'Loading',
          title: t('item.shipping_status.title'),
          onClose: () => setModal(null),
        });
        break;
      case 'error':
        setModal({
          kind: 'Error',
          title: t('item.shipping_status.title'),
          message:
            nonSponsoredShippingLabelMutation.error.response?.data?.message ||
            nonSponsoredShippingLabelMutation.error.message,
          onClose: () => setModal(null),
        });
        break;
      case 'success':
        if (nonSponsoredShippingLabelMutation.data.url) {
          window.open(nonSponsoredShippingLabelMutation.data.url, '_blank');
        }
        refetchItem();
        setModal(null);
        break;
    }
  }, [nonSponsoredShippingLabelMutation.status]);
  // End of old flow
  useEffect(() => {
    if (mutateTracking.kind === 'Completed') {
      refetchItem();
    } else if (mutateTracking.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('item.shipping_status.title'),
        message: t('item.comments.error.description'),
        onClose: () => setModal(null),
      });
    }
  }, [mutateTracking.kind]);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.shipping_status.actions.getShippingLabel'),
      onClick: () => {
        const shipmentDirection = item.unlockReturn ? 'return' : 'forward';

        // shipment from buyer
        if (shipmentDirection === 'return') {
          setModal({ kind: 'ItemGetShippingLabel', item, onClose: () => setModal(null) });
          return;
        }

        // the logic below handles shipments from the seller
        // only shipments from the seller has non-sponsored shipping labels
        if (item.isShipmentSponsored) {
          setModal({ kind: 'ItemGetShippingLabel', item, onClose: () => setModal(null) });
          return;
        }
        nonSponsoredShippingLabelMutation.mutate();
      },
    },
    {
      kind: 'Button',
      label: t('item.shipping_status.actions.shippingReminder'),
      disabled: !!item.inStock,
      onClick: () => {
        setModal({ kind: 'ShippingReminder', productId: item.productId, onClose: () => setModal(null) });
      },
    },
    {
      kind: 'Link',
      label: t('item.shipping_status.actions.tracking'),
      url: item.trackingLink || '/',
      newTab: true,
      disabled: item?.trackingLink === '',
      onClick: () => {
        mutateTracking.mutate('view_shipping_status_tracking');
      },
    },
    {
      kind: 'Button',
      label: t('item.shipping_status.actions.delivered'),
      onClick: () => {
        setModal({ kind: 'ItemConfirmDelivered', item, onClose: () => setModal(null) });
      },
      disabled: !item?.enableConfirmDelivered,
    },
    {
      kind: 'Button',
      label: t('item.shipping_status.actions.cancel_order'),
      onClick: () => setModal({ kind: 'CancelOrder', item, onClose: () => setModal(null) }),
      disabled: item.enableCancelPurchase === false,
    },
    {
      kind: 'Button',
      label: t('item.shipping_status.actions.submit_shipping_label'),
      onClick: () => setModal({ kind: 'ItemSubmitShippingLabel', item, onClose: () => setModal(null) }),
    },
  ];

  const getValueAutoCancelledByLatency = useMemo(() => {
    if (!sellerShippingStatus) return '-';
    if (sellerShippingStatus.isAutoCancelledByScanLatency == null) return 'Null';
    return sellerShippingStatus.isAutoCancelledByScanLatency ? t('common.yes') : t('common.no');
  }, [sellerShippingStatus]);

  const getValueShippedWithinDays = useMemo(() => {
    if (!shippedWithinDays) return '-';
    if (shippedWithinDays.isShippedWithinDays == null) return 'Null';
    return shippedWithinDays.isShippedWithinDays ? t('common.yes') : t('common.no');
  }, [shippedWithinDays]);

  const items = [
    itemText(
      t('item.shipping_status.delivery_leg'),
      item.shipmentInfo.deliveryLeg ? capitalizeFirstLetter(item.shipmentInfo.deliveryLeg) : null,
    ),
    itemText(
      t('item.shipping_status.sponsored'),
      item.isShipmentSponsored == null ? 'N/A' : item.isShipmentSponsored === true ? 'Yes' : 'No',
    ),
    itemText(
      t('item.shipping_status.shipping_method'),
      // Change status display "standard shipping" => Authenticated Shipping
      capitalizeFirstLetter(
        item.shippingMethod?.toLowerCase() === 'standard shipping' ? 'Authenticated Shipping' : item.shippingMethod,
      ),
    ),
    itemText(t('item.shipping_status.carrier'), item.carrierName),
    itemText(t('item.shipping_status.tracking_number'), item.trackingNumber),
    itemText(t('item.shipping_status.warehouse'), item.warehouseName, {
      kind: 'Button',
      label: t('common.update').toUpperCase(),
      onClick: () => {
        mutateTracking.mutate('update_shipping_status_warehouse');
        window.open(getItemShippingStatusWarehouseUpdateUrl(item.productId), '_blank');
      },
    }),
    itemText(t('item.shipping_status.auto_cancellation'), item.isAutoCancelSale ? t('common.on') : t('common.off'), {
      kind: 'Button',
      label: t('common.update').toUpperCase(),
      onClick: () => setModal({ kind: 'ItemOrderAutoCancellation', item, onClose: () => setModal(null) }),
    }),
    itemText(
      t('item.shipping_status.interception_date'),
      item.interceptionDate ? transformDateStringFrance(item.interceptionDate, 'DD/MM/YYYY') : '-',
      {
        kind: 'Button',
        label: t('common.update').toUpperCase(),
        onClick: () => setModal({ kind: 'ItemInterception', item, onClose: () => setModal(null) }),
        disabled: !(item.shippingPlanId == StandardShippingId),
      },
    ),
    itemText(t('item.shipping_status.date_received'), transformDateString(item.dateReceivedAtWareHouse)),
    itemText(t('item.shipping_status.date_received_by_buyer'), transformDateString(item.dateReceived)),
    itemText(t('item.shipping_status.never_received'), item.neverReceived),
    itemText(
      t('item.shipping_status.date_of_lost'),
      item.isLost && item.isLost === '1'
        ? `❌ ${t('item.shipping_status.lost')} \n ${transformDateString(item.dateLost)}`
        : '',
    ),
    itemText(
      t('item.shipping_status.past_shipping_window'),
      item.isPastShippingWindow == null ? 'N/A' : item.isPastShippingWindow === true ? t('common.yes') : t('common.no'),
    ),
    itemText(t('item.shipping_status.shipped_within_day'), getValueShippedWithinDays, {
      kind: 'Button',
      label: t('common.show').toUpperCase(),
      onClick: () => fetchShippedWithinDays(),
      disabled: isFetchingShippedWithinDays,
    }),
    itemText(t('item.shipping_status.is_scan_latency'), getValueAutoCancelledByLatency, {
      kind: 'Button',
      label: t('common.show').toUpperCase(),
      onClick: () => fetchSellerShippingStatus(),
      disabled: isFetchingSellerShippingStatus,
    }),
  ];

  return (
    <>
      <Container
        title={t('item.shipping_status.title')}
        actions={actions}
        testId="shipping_status"
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
